import { reactive, ref, watch } from "@vue/composition-api";
import axios from "axios";
import axiosIns from "@/libs/axios";
import { errorNotification } from "@/utils/toast-notifications";

export default function usrCreateGroup(ctx) {
  const viewMode = !!ctx.root.$route.query.view;
  const showOffer = ref(false);
  const showCampaign = ref(false);
  const groupID = ctx.root.$route.query.id || ctx.root.$route.params.id;
  const targetOptions = ["drivers", "riders"];
  const tableColumns = [
    { label: "phone number", key: "phone_number" },
    "actions",
  ];
  const total = ref();
  const page = ref(1);
  const perPage = ref(10);
  const effectiveTimeVal = ref();
  const expirationTimeVal = ref();
  const campaignEffectiveTimeVal = ref();
  const campaignExpirationTimeVal = ref();
  const createdGroup = reactive({
    name: null,
    description: null,
    target: "drivers",
    locked: false,
    offer: {
      title: {
        en: null,
        ar: null,
      },
      message: {
        en: null,
        ar: null,
      },
      effectiveTime: null,
      expirationTime: null,
      coupon: null,
    },
    campaignOffer: {
      coupon: null,
      logo: null,
      effectiveTime: null,
      expirationTime: null,
      driverMessages: {
        dispatch: {
          message: {
            ar: null,
            en: null,
          },
        },
        arrive: {
          message: {
            ar: null,
            en: null,
          },
        },
      },
      riderMessages: {
        eta: {
          message: {
            ar: null,
            en: null,
          },
        },
        endTrip: {
          message: {
            ar: null,
            en: null,
          },
        },
      },
      isActive: false,
    },
  });
  const openOfferSection = () => {
    showOffer.value = true;
  };
  const closeOfferSection = () => {
    showOffer.value = false;
    createdGroup.offer.title.en = null;
    createdGroup.offer.title.ar = null;
    createdGroup.offer.message.en = null;
    createdGroup.offer.message.ar = null;
    createdGroup.offer.effectiveTime = null;
    createdGroup.offer.expirationTime = null;
    createdGroup.offer.coupon = null;
  };
  const openCampaignSection = () => {
    showCampaign.value = true;
  };
  const closeCampaignSection = () => {
    showCampaign.value = false;
    // Keep the data in state so user can reopen and see their inputs
  };
  const fileInput = ref(null);
  const logoFileInput = ref(null);
  const uploadingLogo = ref(false);
  const loadingCreateBtn = ref(false);
  const createGroupRef = ref(null);
  const createGroup = async () => {
    const isValid = await createGroupRef.value.validate();
    if (!isValid) return;

    // Check if campaign is enabled but no logo is uploaded
    if (
      showCampaign.value &&
      (!createdGroup.campaignOffer.logo ||
        createdGroup.campaignOffer.logo.trim() === "")
    ) {
      errorNotification(
        "Campaign logo is required. Please upload a logo before saving."
      );
      return;
    }

    loadingCreateBtn.value = true;
    try {
      const payloadTemp = JSON.parse(
        JSON.stringify({ ...createdGroup, locked: createdGroup.locked })
      );

      // If target is riders and offer is shown, include offer with dates; otherwise delete offer
      if (createdGroup.target === "riders" && showOffer.value) {
        const temp1 = new Date(
          `${createdGroup.offer.effectiveTime} ${effectiveTimeVal.value}`
        );
        const temp2 = new Date(
          `${createdGroup.offer.expirationTime} ${expirationTimeVal.value}`
        );
        payloadTemp.offer.effectiveTime = temp1;
        payloadTemp.offer.expirationTime = temp2;
      } else {
        delete payloadTemp.offer;
      }
      if (createdGroup.target === "riders" && showCampaign.value) {
        if (
          createdGroup.campaignOffer.effectiveTime &&
          campaignEffectiveTimeVal.value
        ) {
          const temp1 = new Date(
            `${createdGroup.campaignOffer.effectiveTime} ${campaignEffectiveTimeVal.value}`
          );
          payloadTemp.campaignOffer.effectiveTime = temp1;
        }
        if (
          createdGroup.campaignOffer.expirationTime &&
          campaignExpirationTimeVal.value
        ) {
          const temp2 = new Date(
            `${createdGroup.campaignOffer.expirationTime} ${campaignExpirationTimeVal.value}`
          );
          payloadTemp.campaignOffer.expirationTime = temp2;
        }
      } else {
        // Reset campaign data in state when not sending it
        createdGroup.campaignOffer.coupon = null;
        createdGroup.campaignOffer.logo = null;
        createdGroup.campaignOffer.effectiveTime = null;
        createdGroup.campaignOffer.expirationTime = null;
        createdGroup.campaignOffer.driverMessages.dispatch.message.en = null;
        createdGroup.campaignOffer.driverMessages.dispatch.message.ar = null;
        createdGroup.campaignOffer.driverMessages.arrive.message.en = null;
        createdGroup.campaignOffer.driverMessages.arrive.message.ar = null;
        createdGroup.campaignOffer.riderMessages.eta.message.en = null;
        createdGroup.campaignOffer.riderMessages.eta.message.ar = null;
        createdGroup.campaignOffer.riderMessages.endTrip.message.en = null;
        createdGroup.campaignOffer.riderMessages.endTrip.message.ar = null;
        createdGroup.campaignOffer.isActive = false;
        campaignEffectiveTimeVal.value = null;
        campaignExpirationTimeVal.value = null;
        delete payloadTemp.campaignOffer;
      }

      // Check if we're in edit mode
      if (groupID) {
        await ctx.root.$store.dispatch("groups/updateGroup", {
          groupId: groupID,
          data: payloadTemp,
        });
      } else {
        await ctx.root.$store.dispatch("groups/createGroup", payloadTemp);
      }
      ctx.root.$router.push("/groups");
    } catch (error) {
      const errorMsg = error?.response?.data?.error;
      errorNotification(errorMsg);
    } finally {
      loadingCreateBtn.value = false;
    }
  };
  const toBeDeletedUser = ref(null);
  const groupUsers = ref([]);
  const fetchingUsers = ref(false);
  const getGroupUsers = async () => {
    try {
      fetchingUsers.value = true;
      const result = await ctx.root.$store.dispatch("groups/getGroupUsers", {
        groupID,
        page: page.value,
        perPage: perPage.value,
      });
      const { data } = result.data.data;
      groupUsers.value = data.users;
      total.value = data.total;
    } finally {
      fetchingUsers.value = false;
    }
  };

  watch([page, perPage], () => {
    getGroupUsers();
  });
  watch(
    () => createdGroup.target,
    () => {
      if (createdGroup.target === "drivers") {
        closeOfferSection();
        closeCampaignSection();
      }
    }
  );
  const fetchingGroup = ref(false);
  const hasCampaignOffer = ref(false);
  const hasOffer = ref(false);

  // Character limit validation for message fields
  const dispatchEnExceeded = ref(false);
  const dispatchArExceeded = ref(false);
  const arriveEnExceeded = ref(false);
  const arriveArExceeded = ref(false);
  const etaEnExceeded = ref(false);
  const etaArExceeded = ref(false);
  const billEnExceeded = ref(false);
  const billArExceeded = ref(false);
  const getGroup = async () => {
    fetchingGroup.value = true;
    const result = await ctx.root.$store.dispatch("groups/getGroup", groupID);
    const { data } = result.data.data;

    // Track if this group already has offers from the database
    hasCampaignOffer.value = !!data.campaignOffer;
    hasOffer.value = !!data.offer;

    // Handle offer data
    if (data.offer) {
      const effectiveTemp = new Date(data.offer.effectiveTime);
      effectiveTimeVal.value = `${effectiveTemp.getHours()}:${effectiveTemp.getMinutes()}:${effectiveTemp.getSeconds()}`;
      // Extract just the date part (YYYY-MM-DD) for the datepicker in local timezone
      const effYear = effectiveTemp.getFullYear();
      const effMonth = String(effectiveTemp.getMonth() + 1).padStart(2, "0");
      const effDay = String(effectiveTemp.getDate()).padStart(2, "0");
      data.offer.effectiveTime = `${effYear}-${effMonth}-${effDay}`;

      const expirationTemp = new Date(data.offer.expirationTime);
      expirationTimeVal.value = `${expirationTemp.getHours()}:${expirationTemp.getMinutes()}:${expirationTemp.getSeconds()}`;
      // Extract just the date part (YYYY-MM-DD) for the datepicker in local timezone
      const expYear = expirationTemp.getFullYear();
      const expMonth = String(expirationTemp.getMonth() + 1).padStart(2, "0");
      const expDay = String(expirationTemp.getDate()).padStart(2, "0");
      data.offer.expirationTime = `${expYear}-${expMonth}-${expDay}`;
    }

    // Handle campaign offer data
    if (data.campaignOffer) {
      if (data.campaignOffer.effectiveTime) {
        const campaignEffectiveTemp = new Date(
          data.campaignOffer.effectiveTime
        );
        campaignEffectiveTimeVal.value = `${campaignEffectiveTemp.getHours()}:${campaignEffectiveTemp.getMinutes()}:${campaignEffectiveTemp.getSeconds()}`;
        // Extract just the date part (YYYY-MM-DD) for the datepicker in local timezone
        const campEffYear = campaignEffectiveTemp.getFullYear();
        const campEffMonth = String(
          campaignEffectiveTemp.getMonth() + 1
        ).padStart(2, "0");
        const campEffDay = String(campaignEffectiveTemp.getDate()).padStart(
          2,
          "0"
        );
        data.campaignOffer.effectiveTime = `${campEffYear}-${campEffMonth}-${campEffDay}`;
      }
      if (data.campaignOffer.expirationTime) {
        const campaignExpirationTemp = new Date(
          data.campaignOffer.expirationTime
        );
        campaignExpirationTimeVal.value = `${campaignExpirationTemp.getHours()}:${campaignExpirationTemp.getMinutes()}:${campaignExpirationTemp.getSeconds()}`;
        // Extract just the date part (YYYY-MM-DD) for the datepicker in local timezone
        const campExpYear = campaignExpirationTemp.getFullYear();
        const campExpMonth = String(
          campaignExpirationTemp.getMonth() + 1
        ).padStart(2, "0");
        const campExpDay = String(campaignExpirationTemp.getDate()).padStart(
          2,
          "0"
        );
        data.campaignOffer.expirationTime = `${campExpYear}-${campExpMonth}-${campExpDay}`;
      }
    }

    // Set all properties except offer and campaignOffer to preserve structure
    Object.keys(data).forEach((k) => {
      // Skip offer and campaignOffer if they are null to preserve initialized structure
      if ((k === "offer" || k === "campaignOffer") && !data[k]) {
        return;
      }
      createdGroup[k] = data[k];
    });

    showOffer.value = !!data.offer;
    showCampaign.value = !!data.campaignOffer;
    fetchingGroup.value = false;

    await getGroupUsers();
  };
  if (groupID) {
    getGroup();
  }
  const openAddUserModal = () => {
    ctx.root.$bvModal.show("add-users-group");
  };
  const openDeleteUserModal = (val) => {
    toBeDeletedUser.value = val;
    ctx.root.$bvModal.show("delete-user-group");
  };
  const openFile = () => {
    fileInput.value.click();
  };
  const uploading = ref(false);
  const fileHandler = async (event) => {
    try {
      if (event.target.files.length) {
        uploading.value = true;
        const file = event.target.files[0];
        const fileName = file.name;
        const response = await ctx.root.$store.dispatch(
          "groups/getGroupSheetSignedUrl",
          fileName
        );
        const s3Url = response.data.data.url;
        const s3Key = response.data.data.key;
        await axios.put(s3Url, file, {
          headers: {
            "Content-Type": file.type,
          },
        });
        await ctx.root.$store.dispatch("groups/importUsersToGroup", {
          groupID,
          s3Key,
        });
        getGroupUsers();
      }
    } catch (error) {
      console.log(error);
    } finally {
      uploading.value = false;
    }
  };
  const openLogoUpload = () => {
    if (uploadingLogo.value) return;
    logoFileInput.value.click();
  };
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const imageExtensions = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/svg+xml",
    ];
    if (!imageExtensions.includes(file.type)) {
      errorNotification("Invalid file type. Please upload an image file.");
      return;
    }

    uploadingLogo.value = true;
    try {
      // Get signed URL for upload
      const result = await axiosIns.get("/signed-urls", {
        params: {
          fileName: file.name,
          isPublic: true,
        },
      });
      const { url, key, readUrl } = result.data.data;

      // Upload file to S3
      await axios.put(url, file);

      // Store the key and readUrl in campaignOffer.logo
      createdGroup.campaignOffer.logo = readUrl;
    } catch (error) {
      errorNotification("Failed to upload logo. Please try again.");
      console.error("Logo upload error:", error);
    } finally {
      uploadingLogo.value = false;
    }
  };

  // Input handlers for character limit validation
  const handleDispatchEnInput = () => {
    dispatchEnExceeded.value =
      (createdGroup.campaignOffer.driverMessages.dispatch.message.en || "")
        .length > 80;
  };

  const handleDispatchArInput = () => {
    dispatchArExceeded.value =
      (createdGroup.campaignOffer.driverMessages.dispatch.message.ar || "")
        .length > 80;
  };

  const handleArriveEnInput = () => {
    arriveEnExceeded.value =
      (createdGroup.campaignOffer.driverMessages.arrive.message.en || "")
        .length > 80;
  };

  const handleArriveArInput = () => {
    arriveArExceeded.value =
      (createdGroup.campaignOffer.driverMessages.arrive.message.ar || "")
        .length > 80;
  };

  const handleEtaEnInput = () => {
    etaEnExceeded.value =
      (createdGroup.campaignOffer.riderMessages.eta.message.en || "").length >
      80;
  };

  const handleEtaArInput = () => {
    etaArExceeded.value =
      (createdGroup.campaignOffer.riderMessages.eta.message.ar || "").length >
      80;
  };

  const handleBillEnInput = () => {
    billEnExceeded.value =
      (createdGroup.campaignOffer.riderMessages.endTrip.message.en || "")
        .length > 80;
  };

  const handleBillArInput = () => {
    billArExceeded.value =
      (createdGroup.campaignOffer.riderMessages.endTrip.message.ar || "")
        .length > 80;
  };
  return {
    createGroup,
    fetchingGroup,
    createdGroup,
    openDeleteUserModal,
    tableColumns,
    uploading,
    fetchingUsers,
    fileHandler,
    targetOptions,
    loadingCreateBtn,
    fileInput,
    openAddUserModal,
    toBeDeletedUser,
    effectiveTimeVal,
    expirationTimeVal,
    campaignEffectiveTimeVal,
    campaignExpirationTimeVal,
    getGroupUsers,
    groupUsers,
    createGroupRef,
    viewMode,
    openFile,
    total,
    page,
    perPage,
    groupID,
    showOffer,
    openOfferSection,
    closeOfferSection,
    showCampaign,
    openCampaignSection,
    closeCampaignSection,
    logoFileInput,
    uploadingLogo,
    openLogoUpload,
    handleLogoUpload,
    hasCampaignOffer,
    hasOffer,
    // Character limit validation
    dispatchEnExceeded,
    dispatchArExceeded,
    arriveEnExceeded,
    arriveArExceeded,
    etaEnExceeded,
    etaArExceeded,
    billEnExceeded,
    billArExceeded,
    handleDispatchEnInput,
    handleDispatchArInput,
    handleArriveEnInput,
    handleArriveArInput,
    handleEtaEnInput,
    handleEtaArInput,
    handleBillEnInput,
    handleBillArInput,
  };
}
